// ─── App switcher ─────────────────────────────────────────────────────────────
// Every storefront in the family, for the dropdown in the header. Identical in all
// seven apps (the six CRA shops and the LMU TV storefront inside the hub) — copy it
// over rather than editing one copy, so the menus cannot drift apart again. They had:
// nobody linked ACE DRIVE, ACC ACADEMY was missing two entries, iR DRIVE was listed
// under its old host, and LMU TV was in nobody's menu while its own menu was missing
// AC DRIVE and ACE DRIVE.
//
// The logo is read from the target site's own /logo192.png instead of being bundled
// here. A redesigned logo then has to be deployed once, on the app it belongs to,
// rather than copied into seven repos and rebuilt seven times. LMU TV is the one
// exception: it is served from a path on the hub, whose /logo192.png belongs to
// SimRacing Solutions, so it names its own file.

export interface AppLink {
  name: string;
  url: string;
  logo: string;
  /** Path prefix, for an app served from a subpath instead of its own host. */
  path?: string;
  /** Other hosts the same app answers on, so its own menu never links to itself. */
  aliases?: string[];
}

const SITES: Array<Omit<AppLink, "logo"> & { logo?: string }> = [
  { name: "ACC DRIVE", url: "https://accdrive.com" },
  { name: "ACC ACADEMY", url: "https://academy.accsolutions.de" },
  { name: "iR DRIVE", url: "https://irdrive.de" },
  { name: "LMU DRIVE", url: "https://lmudrive.com" },
  { name: "AC DRIVE", url: "https://acdrive.simracingsolutions.de" },
  { name: "ACE DRIVE", url: "https://acedrive.simracingsolutions.de" },
  { name: "ACC TV", url: "https://acctv.de" },
  {
    name: "LMU TV",
    url: "https://simracingsolutions.de/lmutv",
    logo: "https://simracingsolutions.de/lmutv-logo192.png",
    path: "/lmutv",
    aliases: ["lmutv.de", "www.lmutv.de", "lmutv.com", "www.lmutv.com"],
  },
];

export const APPS: AppLink[] = SITES.map((site) => ({
  ...site,
  logo: site.logo ?? `${site.url}/logo192.png`,
}));

/**
 * Every app except the one currently being viewed, matched on the host. Doing it by
 * host instead of a per-app constant is what keeps this file byte-identical across the
 * repos. On localhost nothing matches and the full list shows, which is harmless.
 *
 * An app on a subpath (LMU TV on the hub) only counts as "current" when the path
 * matches too — otherwise the hub's own pages would drop it from their menu — and it
 * also hides itself on the alternate hosts it answers on.
 */
export function otherApps(
  host: string = window.location.hostname,
  pathname: string = window.location.pathname
): AppLink[] {
  return APPS.filter((app) => {
    try {
      if (app.aliases?.includes(host)) return false;
      if (new URL(app.url).hostname !== host) return true;
      if (!app.path) return false;
      return !(pathname === app.path || pathname.startsWith(`${app.path}/`));
    } catch {
      return true;
    }
  });
}
